import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import type { ReportVisual } from '../../services/reportsApi';
import ReportEmptyState from './ReportEmptyState';

type ReportSeverityMixProps = {
  visual: ReportVisual;
  compact?: boolean;
};

function toneColor(tone: unknown): 'error' | 'warning' | 'info' | 'success' | 'default' {
  const normalized = String(tone || '').trim().toLowerCase();
  if (normalized === 'error' || normalized === 'critical' || normalized === 'red') return 'error';
  if (normalized === 'warning' || normalized === 'amber' || normalized === 'high') return 'warning';
  if (normalized === 'success' || normalized === 'green' || normalized === 'low') return 'success';
  if (normalized === 'info' || normalized === 'medium' || normalized === 'blue') return 'info';
  return 'default';
}

const ReportSeverityMix: React.FC<ReportSeverityMixProps> = ({ visual, compact = false }) => {
  const items = Array.isArray(visual.data?.items) ? visual.data.items : [];
  const total = Number(visual.data?.total || 0);

  return (
    <Paper variant="outlined" sx={{ p: compact ? 1.5 : 2, height: '100%' }}>
      <Typography variant="h6">{visual.title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: compact ? 1 : 1.5 }}>
        {visual.insight}
      </Typography>

      {visual.empty_state ? (
        <ReportEmptyState
          title={visual.empty_state.title}
          detail={visual.empty_state.detail}
          missingData={visual.empty_state.missing_data}
          compact={compact}
        />
      ) : (
        <>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: compact ? 1 : 1.5 }}>
            {items.map((item: Record<string, unknown>, index: number) => (
              <Chip
                key={String(item?.id || index)}
                label={`${String(item?.label || 'Unknown')}: ${Number(item?.value || 0)}`}
                size="small"
                variant="outlined"
                color={toneColor(item?.tone)}
              />
            ))}
          </Stack>
          <Stack spacing={compact ? 0.75 : 1}>
            {items.map((item: Record<string, unknown>, index: number) => {
              const value = Number(item?.value || 0);
              const widthPct = total > 0 ? Math.max(4, (value / total) * 100) : 0;
              return (
                <Box key={String(item?.id || index)}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2">{String(item?.label || 'Unknown')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {value}
                    </Typography>
                  </Box>
                  <Box sx={{ height: 12, bgcolor: 'grey.100', borderRadius: 999 }}>
                    <Box
                      sx={{
                        height: '100%',
                        width: `${widthPct}%`,
                        borderRadius: 999,
                        bgcolor:
                          toneColor(item?.tone) === 'error'
                            ? 'error.main'
                            : toneColor(item?.tone) === 'warning'
                              ? 'warning.main'
                              : toneColor(item?.tone) === 'success'
                                ? 'success.main'
                                : 'primary.main',
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </>
      )}
    </Paper>
  );
};

export default ReportSeverityMix;
